import { useEffect, useState } from "react";

import { updateBranch } from "../api/branches";
import { NoScope, PaneHead, scopeProblem } from "./pane";
import { useSettingsScope } from "./settings-layout";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { useWriter } from "./use-writer";

/** 00:00 to 23:00 for the start, 01:00 to 24:00 for the end — whole hours, in minutes. */
const STARTS = Array.from({ length: 24 }, (_, hour) => hour * 60);
const ENDS = Array.from({ length: 24 }, (_, hour) => (hour + 1) * 60);

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:00`;
}

/**
 * **What the calendar shows, per location** (STORY-177, Anthony 2026-10-11).
 *
 * The day grid drew all twenty-four hours and was far taller than a screen. It
 * now shows a chosen range — 08:00 to 20:00 until somebody changes it — and a
 * chosen zoom. Both are display only and refuse no booking.
 *
 * **A booking outside the range is never hidden**: that day's grid stretches by
 * whole hours to hold it. So narrowing the hours cannot lose an appointment, and
 * the hint says so, because "where did my 07:30 go?" is the question this
 * setting would otherwise invite.
 *
 * Per location from the rail's switcher, like Reminders and Opening hours;
 * clinic-scoped on the wire (`PATCH /branches/:id`).
 */
export function CalendarViewPane(): React.JSX.Element {
  const { branches, branch, reloadBranches } = useSettingsScope();
  const problem = scopeProblem(branches, branch);
  const { busy, toast, dismiss, run } = useWriter();

  // From the location's own row — nothing here invents a default.
  const [opens, setOpens] = useState(branch?.calendarOpensAt ?? 0);
  const [closes, setCloses] = useState(branch?.calendarClosesAt ?? 0);
  const [zoom, setZoom] = useState(branch?.calendarZoom ?? 100);

  useEffect(() => {
    if (branch === null) return;
    setOpens(branch.calendarOpensAt);
    setCloses(branch.calendarClosesAt);
    setZoom(branch.calendarZoom);
  }, [branch]);

  const changed =
    branch !== null &&
    (opens !== branch.calendarOpensAt ||
      closes !== branch.calendarClosesAt ||
      zoom !== branch.calendarZoom);
  const rangeOk = closes > opens;

  return (
    <>
      <PaneHead title="Calendar" scope={branch?.name} />

      {problem !== null && problem !== "not-a-member" ? (
        <NoScope what="the calendar view" reason={problem} />
      ) : branch === null ? null : (
        <section className={styles.card} data-testid="calendar-view-pane">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>What the calendar shows</h2>
          </div>

          <div className={styles.cardBody}>
            <p className={styles.lead}>
              The day view draws these hours, at this zoom, for everyone at this location. It
              changes only what is drawn: a booking outside the hours is never hidden, because that
              day&rsquo;s calendar stretches to show it.
            </p>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="calendar-opens">
                Starts at
              </label>
              <select
                id="calendar-opens"
                className={styles.input}
                value={opens}
                disabled={busy}
                onChange={(event) => setOpens(Number(event.target.value))}
                data-testid="calendar-opens"
              >
                {STARTS.map((value) => (
                  <option key={value} value={value}>
                    {clock(value)}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="calendar-closes">
                Ends at
              </label>
              <select
                id="calendar-closes"
                className={styles.input}
                value={closes}
                disabled={busy}
                onChange={(event) => setCloses(Number(event.target.value))}
                data-testid="calendar-closes"
              >
                {ENDS.map((value) => (
                  <option key={value} value={value}>
                    {clock(value)}
                  </option>
                ))}
              </select>
              {!rangeOk && (
                <p className={styles.formNote} data-testid="calendar-range-error">
                  It has to end after it starts.
                </p>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="calendar-zoom">
                Zoom: {zoom}%
              </label>
              <input
                id="calendar-zoom"
                type="range"
                min={40}
                max={150}
                step={5}
                value={zoom}
                disabled={busy}
                onChange={(event) => setZoom(Number(event.target.value))}
                data-testid="calendar-zoom"
              />
              <p className={styles.formNote}>
                Smaller shows more of the day on screen; larger makes short appointments easier to
                read.
              </p>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.primary}
                disabled={busy || !changed || !rangeOk}
                onClick={() => {
                  void run(
                    () =>
                      updateBranch(branch.id, {
                        calendarOpensAt: opens,
                        calendarClosesAt: closes,
                        calendarZoom: zoom,
                      }),
                    reloadBranches,
                    `The calendar shows ${clock(opens)} to ${clock(closes)} at ${String(zoom)}%.`,
                  );
                }}
                data-testid="calendar-view-save"
              >
                Save calendar view
              </button>
            </div>
          </div>
        </section>
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}
